import { useCallback, useEffect, useState } from 'react';

import { AccessControlPermission, AccessControlResourcePatchRequest } from '@futrli-sdk/predict';
import { AccessControlResourceResponseWithOwnerName } from '@futrli/views/processors/sharing/resourcePermissions';

export type DefaultPermission = AccessControlResourcePatchRequest['default_permission'];
export type ExplicitPermissionLevel = Exclude<AccessControlPermission['level'], 'no-access'>;

export const useShareResourceForm = (resourcePermissions?: AccessControlResourceResponseWithOwnerName) => {
  const [defaultPermission, setDefaultPermission] = useState<DefaultPermission>('owner-only');
  const [permissions, setPermissions] = useState<AccessControlPermission[]>([]);

  useEffect(() => {
    if (!resourcePermissions) {
      return;
    }

    setDefaultPermission(resourcePermissions.default_permission);
    setPermissions(resourcePermissions.permissions);
  }, [resourcePermissions]);

  const addMember = useCallback((userId: string) => {
    setPermissions(currentPermissions => {
      const existingPermission = currentPermissions.find(permission => permission.user_id === userId);

      if (existingPermission) {
        return currentPermissions;
      }

      return [...currentPermissions, { level: 'no-access', user_id: userId }];
    });
  }, []);

  const removeMember = useCallback((userId: string) => {
    setPermissions(currentPermissions => currentPermissions.filter(permission => permission.user_id !== userId));
  }, []);

  const setMemberPermission = useCallback((userId: string, level: ExplicitPermissionLevel) => {
    setPermissions(currentPermissions =>
      currentPermissions.map(permission => (permission.user_id === userId ? { ...permission, level } : permission))
    );
  }, []);

  const getRequestData = useCallback(
    (): AccessControlResourcePatchRequest => ({
      default_permission: defaultPermission,
      permissions
    }),
    [defaultPermission, permissions]
  );

  return {
    addMember,
    defaultPermission,
    getRequestData,
    permissions,
    removeMember,
    setDefaultPermission,
    setMemberPermission
  };
};
